import {ProfileProvider} from 'contexts/ProfileContext';
import {useMemo, useState} from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import {GenericBrandedWrapper} from './Login';
import {
  doc,
  getFirestore,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import {useDocumentData} from 'react-firebase-hooks/firestore';
import {Loading} from 'components/loading/Loading';
import {useShowDeveloperButtonIfHidden} from 'contexts/DeveloperToolsContext';
import {useTranslateFunction} from 'contexts/translation/TranslationContext';
import {useAuthUid} from 'hooks/user/useUserId';
import {
  OPTIONAL_EMAIL_CATEGORIES,
  isEmailCategoryOn,
  parseEmailPreferences,
  type OptionalEmailCategory,
} from '@cip/core/users';
import {
  planCategorySwitch,
  planTurnOffAll,
  type EmailPreferenceUpdate,
} from './emailPreferenceWrites';

/** What each optional category's switch is called and what it sends. */
const CATEGORY_COPY: Record<
  OptionalEmailCategory,
  {label: string; description: string}
> = {
  catchUpReminders: {
    label: 'Catch-up reminders',
    description: "When course work you haven't finished is waiting.",
  },
  courseNews: {
    label: 'Celebrations and course news',
    description: 'Milestones you reach, and news about your course.',
  },
  sectionReminders: {
    label: 'Section reminders',
    description: 'Before your section meets.',
  },
  forumReplies: {
    label: 'Forum replies',
    description: 'When someone replies to you in the course forum.',
  },
  futureCourseNews: {
    label: 'News about future courses',
    description: 'When a new course opens.',
  },
};

/**
 * The email preferences page at `/unsubscribe`: course essentials, which
 * always come, then a switch for each optional category.
 *
 * @return The rendered page.
 * @throws Never.
 */
export const Unsubscribe = () => {
  useShowDeveloperButtonIfHidden();

  return (
    <ProfileProvider>
      <GenericBrandedWrapper>
        <EmailPreferencesForm />
      </GenericBrandedWrapper>
    </ProfileProvider>
  );
};

/** Where the last save stands, for the status line under the switches. */
type SaveStatus = 'idle' | 'saving' | 'saved' | 'failed';

/**
 * The signed-in person's email preferences, read live from `users/{uid}` and
 * saved on every change.
 *
 * @return The rendered preferences.
 * @throws Never.
 */
export function EmailPreferencesForm() {
  const t = useTranslateFunction('miscUI');
  const uid = useAuthUid();
  const userRef = useMemo(
    () => (uid === null ? null : doc(getFirestore(), 'users', uid)),
    [uid],
  );
  const [userDoc, isLoading, loadError] = useDocumentData(userRef);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');

  if (isLoading) {
    return <Loading />;
  }
  if (userRef === null || loadError !== undefined) {
    return (
      <p role="alert">
        {t("We couldn't load your email preferences. Reload to try again.")}
      </p>
    );
  }

  const preferences = parseEmailPreferences(userDoc);
  const isAllOff = OPTIONAL_EMAIL_CATEGORIES.every(
    category => !isEmailCategoryOn({preferences, category}),
  );

  const save = async (update: EmailPreferenceUpdate) => {
    setSaveStatus('saving');
    try {
      await updateDoc(userRef, {
        ...update,
        emailPreferencesSavedAt: serverTimestamp(),
      });
      setSaveStatus('saved');
    } catch (error) {
      console.warn('[email-preferences] save failed', error);
      setSaveStatus('failed');
    }
  };

  return (
    <div className="d-flex flex-column w-100 gap-3 px-2">
      <h2 className="h4 text-center">{t('Email preferences')}</h2>

      <section aria-labelledby="email-pref-essentials">
        <h3 className="h6 mb-1" id="email-pref-essentials">
          {t('Course essentials')}
        </h3>
        <p className="mb-0">
          {t(
            'Always on. Emails about your section, schedule changes, your account and your certificate.',
          )}
        </p>
      </section>

      <fieldset>
        <legend className="h6 mb-2">{t('Optional emails')}</legend>
        <div className="d-flex flex-column gap-3">
          {OPTIONAL_EMAIL_CATEGORIES.map(category => {
            const isOn = isEmailCategoryOn({preferences, category});
            const switchId = `email-pref-${category}`;
            const descriptionId = `${switchId}-description`;
            return (
              <div key={category}>
                <Form.Check
                  type="switch"
                  role="switch"
                  id={switchId}
                  label={t(CATEGORY_COPY[category].label)}
                  checked={isOn}
                  aria-describedby={descriptionId}
                  onChange={() => {
                    void save(
                      planCategorySwitch({preferences, category, isOn: !isOn}),
                    );
                  }}
                />
                <p className="small text-muted mb-0" id={descriptionId}>
                  {t(CATEGORY_COPY[category].description)}
                </p>
              </div>
            );
          })}
        </div>
      </fieldset>

      <Button
        variant="outline-secondary"
        disabled={isAllOff}
        onClick={() => {
          void save(planTurnOffAll());
        }}
      >
        {t('Turn off all optional emails')}
      </Button>

      <p role="status" className="small mb-0">
        {saveStatus === 'saving' && t('Saving…')}
        {saveStatus === 'saved' && t('Saved.')}
        {saveStatus === 'failed' &&
          t("Couldn't save. Check your connection and try again.")}
      </p>
    </div>
  );
}
